import { initMediaReveals, preparePoster } from "./media-readiness.js";

(() => {
  document.documentElement.classList.add("case-js");
  const prepareMedia = initMediaReveals();

  const reveals = [...document.querySelectorAll("[data-reveal]")];
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: "0px 0px -12%", threshold: 0.12 },
  );

  reveals.forEach((element) => observer.observe(element));

  const loopVideos = [
    ...document.querySelectorAll(".case-loop video:not([data-media-playback='custom']), [data-autoplay-loop]"),
  ];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saveData = navigator.connection?.saveData === true;
  const manuallyPausedVideos = new WeakSet();

  loopVideos.forEach((video) => {
    const mediaContainer = video.closest(".case-loop, .chapter-media");
    const label = video.getAttribute("aria-label")
      || mediaContainer?.getAttribute("aria-label")
      || mediaContainer?.querySelector("figcaption span")?.textContent.trim()
      || "video";
    // Shared case-study loops stay free of overlays unless explicitly requested.
    const toggle = video.dataset.loopControls === "button" ? document.createElement("button") : null;
    if (toggle) {
      const icon = document.createElement("span");
      toggle.className = "loop-video-toggle";
      toggle.type = "button";
      icon.className = "loop-video-toggle-icon";
      icon.setAttribute("aria-hidden", "true");
      toggle.append(icon);
      mediaContainer?.append(toggle);
    }

    const syncPlaybackState = () => {
      const isPlaying = !video.paused && !video.ended;
      mediaContainer?.classList.toggle("is-video-playing", isPlaying);
      toggle?.classList.toggle("is-playing", isPlaying);
      toggle?.setAttribute("aria-label", `${isPlaying ? "Pause" : "Play"} ${label}`);
    };
    const markReady = () => {
      mediaContainer?.classList.add("is-media-ready");
      syncPlaybackState();
    };

    toggle?.addEventListener("click", () => {
      if (!video.paused && !video.ended) {
        manuallyPausedVideos.add(video);
        video.pause();
        return;
      }

      manuallyPausedVideos.delete(video);
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
        video.load();
      }
      video.play().catch(syncPlaybackState);
    });
    video.addEventListener("playing", markReady);
    video.addEventListener("pause", syncPlaybackState);
    video.addEventListener("ended", syncPlaybackState);
    syncPlaybackState();

    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      markReady();
    }
  });

  if (loopVideos.length) {
    if (reduceMotion || saveData) {
      loopVideos.forEach((video) => {
        video.pause();
        video.removeAttribute("autoplay");
        video.preload = "none";
      });
    } else {
      const visibleLoopVideos = new Set();
      const playVisibleVideo = async (video) => {
        if (video.poster) {
          await preparePoster(video);
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        }
        if (visibleLoopVideos.has(video)
          && document.visibilityState === "visible"
          && !manuallyPausedVideos.has(video)) {
          video.play().catch(() => {});
        }
      };
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              visibleLoopVideos.add(video);
            } else {
              visibleLoopVideos.delete(video);
            }

            if (
              entry.isIntersecting
              && document.visibilityState === "visible"
              && !manuallyPausedVideos.has(video)
            ) {
              playVisibleVideo(video);
            } else {
              video.pause();
            }
          });
        },
        { rootMargin: "260px 0px", threshold: 0.01 },
      );

      loopVideos.forEach((video) => videoObserver.observe(video));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          visibleLoopVideos.forEach((video) => {
            if (!manuallyPausedVideos.has(video)) {
              playVisibleVideo(video);
            }
          });
        } else {
          loopVideos.forEach((video) => video.pause());
        }
      });
    }
  }

  const filmPlayer = document.querySelector("[data-film-player]");
  const fullFilm = filmPlayer?.querySelector("[data-full-film]");
  const filmPlay = filmPlayer?.querySelector("[data-film-play]");

  if (filmPlayer && fullFilm && filmPlay) {
    const resetFilm = () => {
      fullFilm.controls = false;
      fullFilm.currentTime = 0;
      filmPlayer.classList.remove("is-playing");
      filmPlay.hidden = false;
    };

    filmPlay.addEventListener("click", () => {
      fullFilm.controls = true;
      filmPlayer.classList.add("is-playing");
      filmPlay.hidden = true;
      if (fullFilm.networkState === HTMLMediaElement.NETWORK_EMPTY) {
        fullFilm.load();
      }
      fullFilm.play().catch(resetFilm);
    });

    fullFilm.addEventListener("ended", resetFilm);
  }

  const breadthStory = document.querySelector("[data-breadth-story]");
  const breadthFrames = [
    ...(breadthStory?.querySelectorAll("[data-breadth-frame]") || []),
  ];
  const breadthCounter = breadthStory?.querySelector("[data-breadth-counter]");
  let breadthFrame = 0;
  let activeBreadthFrame = breadthFrames.findIndex((frame) => frame.classList.contains("is-active"));
  let requestedBreadthFrame = activeBreadthFrame;
  let pendingBreadthFrame = -1;

  const setBreadthFrame = (index) => {
    requestedBreadthFrame = index;
    if (index === activeBreadthFrame) return;
    const nextFrame = breadthFrames[index];
    // Keep the current image and caption while a requested frame is decoding.
    if (nextFrame?.hasAttribute("data-media-reveal") && !nextFrame.classList.contains("is-image-ready")) {
      if (index !== pendingBreadthFrame) {
        pendingBreadthFrame = index;
        prepareMedia(nextFrame).then((loaded) => {
          if (pendingBreadthFrame === index) pendingBreadthFrame = -1;
          if (loaded && requestedBreadthFrame === index) setBreadthFrame(index);
        });
      }
      return;
    }
    activeBreadthFrame = index;

    breadthFrames.forEach((frame, frameIndex) => {
      const isActive = frameIndex === index;
      frame.classList.toggle("is-active", isActive);
      frame.setAttribute("aria-hidden", String(!isActive));
    });

    if (breadthCounter) {
      const current = String(index + 1).padStart(2, "0");
      const total = String(breadthFrames.length).padStart(2, "0");
      breadthCounter.textContent = `${current} / ${total}`;
    }
  };

  const renderBreadthStory = () => {
    breadthFrame = 0;
    if (!breadthStory || !breadthFrames.length) return;

    const bounds = breadthStory.getBoundingClientRect();
    const scrollableDistance = Math.max(1, bounds.height - window.innerHeight);
    const progress = Math.min(1, Math.max(0, -bounds.top / scrollableDistance));
    const index = Math.min(
      breadthFrames.length - 1,
      Math.floor(progress * breadthFrames.length),
    );
    setBreadthFrame(index);
  };

  const requestBreadthStory = () => {
    if (!breadthFrame) breadthFrame = requestAnimationFrame(renderBreadthStory);
  };

  if (breadthStory && breadthFrames.length) {
    window.addEventListener("scroll", requestBreadthStory, { passive: true });
    window.addEventListener("resize", requestBreadthStory, { passive: true });
    renderBreadthStory();
  }

  const studioStory = document.querySelector("[data-studio-story]");
  const studioFrames = [...(studioStory?.querySelectorAll("[data-studio-frame]") || [])];
  const studioBeats = [...(studioStory?.querySelectorAll("[data-studio-beat]") || [])];
  const studioCounter = studioStory?.querySelector("[data-studio-counter]");
  const studioScroll = studioStory?.querySelector(".studios-scroll");
  const studioMobile = window.matchMedia("(max-width: 900px)");
  let studioFrame = 0;
  let activeStudioBeat = -1;
  let activeStudioFrame = -1;
  let requestedStudioState = null;
  let pendingStudioFrame = -1;
  let studioPinned = false;
  let studioOffset = 0;
  let studioTravel = 1;
  let studioTop = 0;

  const setStudioState = (beatIndex, frameIndex) => {
    requestedStudioState = { beatIndex, frameIndex };
    const nextFrame = studioFrames[frameIndex];
    if (nextFrame?.hasAttribute("data-media-reveal") && !nextFrame.classList.contains("is-active") && !nextFrame.classList.contains("is-image-ready")) {
      if (pendingStudioFrame !== frameIndex) {
        pendingStudioFrame = frameIndex;
        prepareMedia(nextFrame).then((loaded) => {
          if (pendingStudioFrame === frameIndex) pendingStudioFrame = -1;
          if (loaded && requestedStudioState.frameIndex === frameIndex) {
            setStudioState(requestedStudioState.beatIndex, frameIndex);
          }
        });
      }
      return;
    }
    if (beatIndex !== activeStudioBeat) {
      activeStudioBeat = beatIndex;
      studioBeats.forEach((beat, index) => {
        beat.classList.toggle("is-active", index === beatIndex);
      });
    }

    if (frameIndex === activeStudioFrame) return;
    activeStudioFrame = frameIndex;

    studioFrames.forEach((frame, index) => {
      const isActive = index === frameIndex;
      frame.classList.toggle("is-active", isActive);
      frame.setAttribute("aria-hidden", String(!isActive && !studioStory.classList.contains("is-studio-stacked")));
    });

    if (studioCounter) {
      const current = String(frameIndex + 1).padStart(2, "0");
      const total = String(studioFrames.length).padStart(2, "0");
      studioCounter.textContent = `${current} / ${total}`;
    }
  };

  const renderStudioStory = () => {
    studioFrame = 0;
    if (!studioStory || !studioBeats.length) return;

    if (studioPinned) {
      const progress = Math.min(0.999, Math.max(0,
        (studioTop - studioStory.getBoundingClientRect().top - studioOffset) / studioTravel,
      ));
      const beatIndex = Math.floor(progress * studioBeats.length);
      const frames = studioBeats[beatIndex].dataset.studioFrames.split(",").map(Number);
      const withinBeat = progress * studioBeats.length - beatIndex;
      setStudioState(beatIndex, frames[Math.floor(withinBeat * frames.length)]);
      return;
    }

    const viewportTarget = window.innerHeight * 0.56;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    let containingIndex = -1;

    studioBeats.forEach((beat, index) => {
      const bounds = beat.getBoundingClientRect();
      if (bounds.top <= viewportTarget && bounds.bottom >= viewportTarget) {
        containingIndex = index;
      }
      const distance = Math.abs(bounds.top + bounds.height * 0.5 - viewportTarget);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const beatIndex = containingIndex >= 0 ? containingIndex : closestIndex;
    const activeBeat = studioBeats[beatIndex];
    const frameIndexes = activeBeat.dataset.studioFrames
      .split(",")
      .map((value) => Number.parseInt(value, 10));
    let frameIndex = frameIndexes[0];

    if (frameIndexes.length > 1) {
      const bounds = activeBeat.getBoundingClientRect();
      const progress = Math.min(
        0.999,
        Math.max(0, (viewportTarget - bounds.top) / bounds.height),
      );
      frameIndex = frameIndexes[Math.floor(progress * frameIndexes.length)];
    }

    setStudioState(beatIndex, frameIndex);
  };

  const requestStudioStory = () => {
    if (!studioFrame) studioFrame = requestAnimationFrame(renderStudioStory);
  };

  const updateStudioLayout = () => {
    studioPinned = false;
    activeStudioFrame = -1;
    studioStory.classList.remove("is-studio-stacked");
    studioStory.classList.toggle("is-mobile-studio", studioMobile.matches);
    studioStory.style.removeProperty("--studio-content-height");

    if (studioMobile.matches) {
      studioStory.style.setProperty("--studio-steps", studioBeats.length);
      studioScroll.style.position = "static";
      studioOffset = studioScroll.offsetTop;
      studioTop = Number.parseFloat(getComputedStyle(studioScroll).top) || 0;
      const panelHeight = studioScroll.offsetHeight;
      studioScroll.style.removeProperty("position");
      // Pin the image and copy as one unit only when both fit on screen.
      studioPinned = panelHeight + studioTop + 24 <= window.innerHeight;
      studioStory.classList.toggle("is-studio-stacked", !studioPinned);
      if (studioPinned) {
        studioStory.style.setProperty("--studio-content-height", `${studioOffset + panelHeight}px`);
        studioTravel = Math.max(1, studioStory.offsetHeight - studioOffset - panelHeight);
      }
    }

    requestStudioStory();
  };

  if (studioStory && studioScroll && studioFrames.length && studioBeats.length) {
    studioFrames.forEach((frame, index) => frame.style.setProperty("--studio-order", index));
    studioBeats.forEach((beat, index) => beat.style.setProperty("--studio-order", index));
    window.addEventListener("scroll", requestStudioStory, { passive: true });
    window.addEventListener("resize", updateStudioLayout, { passive: true });
    const studioResize = new ResizeObserver(updateStudioLayout);
    studioStory.querySelectorAll(".studios-heading, .studio-beat-copy").forEach((element) => studioResize.observe(element));
    updateStudioLayout();
  }

  const parallaxMedia = document.body.dataset.mediaParallax === "off" ? []
    : [...document.querySelectorAll(".chapter-media")].filter((media) => !media.closest("[data-media-stack]"));
  const parallaxMotion = window.matchMedia(
    "(min-width: 821px) and (prefers-reduced-motion: no-preference)",
  );
  const lowPowerParallax = window.matchMedia("(update: slow)").matches
    || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
    || (navigator.deviceMemory && navigator.deviceMemory <= 4);
  let parallaxFrame = 0;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const renderMediaParallax = () => {
    parallaxFrame = 0;

    if (!parallaxMotion.matches || lowPowerParallax) return;

    const viewportHeight = window.innerHeight;
    parallaxMedia.forEach((media) => {
      const bounds = media.getBoundingClientRect();
      const progress = clamp(
        (viewportHeight - bounds.top) / (viewportHeight + bounds.height),
        0,
        1,
      );
      const arrival = clamp(
        (viewportHeight - bounds.top) / (viewportHeight * 0.5),
        0,
        1,
      );
      const offset = (progress - 0.5) * 18;
      const scale = 1.078 - arrival * 0.014;

      media.style.setProperty("--media-parallax-y", `${offset.toFixed(2)}px`);
      media.style.setProperty("--media-parallax-scale", scale.toFixed(4));
    });
  };

  const requestMediaParallax = () => {
    if (!parallaxFrame) parallaxFrame = requestAnimationFrame(renderMediaParallax);
  };

  const updateMediaParallaxMode = () => {
    const enabled = parallaxMotion.matches && !lowPowerParallax;
    document.documentElement.classList.toggle("case-media-parallax", enabled);

    if (enabled) {
      requestMediaParallax();
    } else {
      parallaxMedia.forEach((media) => {
        media.style.removeProperty("--media-parallax-y");
        media.style.removeProperty("--media-parallax-scale");
      });
    }
  };

  if (parallaxMedia.length) {
    window.addEventListener("scroll", requestMediaParallax, { passive: true });
    window.addEventListener("resize", requestMediaParallax, { passive: true });
    parallaxMotion.addEventListener("change", updateMediaParallaxMode);
    updateMediaParallaxMode();
  }

  const libraryExplorer = document.querySelector("[data-library-explorer]");
  const libraryFrame = document.querySelector("[data-library-frame]");
  const libraryImage = document.querySelector("[data-library-image]");
  const libraryFocusImage = document.querySelector("[data-library-focus-image]");
  const libraryMotion = window.matchMedia(
    "(min-width: 821px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)",
  );

  if (libraryExplorer && libraryFrame && libraryImage && libraryFocusImage) {
    const overview = { x: 0.5, y: 0.39, scale: 1 };
    const loupeScale = 1.65;
    const current = { ...overview };
    const target = { ...overview };
    let animationFrame = 0;
    let isZoomed = false;

    const renderLibrary = () => {
      animationFrame = 0;
      if (!libraryMotion.matches) return;

      const frameWidth = libraryFrame.clientWidth;
      const frameHeight = libraryFrame.clientHeight;
      const imageWidth = libraryImage.clientWidth;
      const imageHeight = libraryImage.clientHeight;
      target.scale = isZoomed ? loupeScale : 1;

      current.x += (target.x - current.x) * 0.11;
      current.y += (target.y - current.y) * 0.11;
      current.scale += (target.scale - current.scale) * 0.09;

      const scaledWidth = imageWidth * current.scale;
      const scaledHeight = imageHeight * current.scale;
      const targetX = frameWidth * 0.5 - current.x * scaledWidth;
      const targetY = frameHeight * 0.5 - current.y * scaledHeight;
      const translateX = clamp(targetX, frameWidth - scaledWidth, 0);
      const translateY = clamp(targetY, frameHeight - scaledHeight, 0);

      const transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${current.scale})`;
      libraryImage.style.transform = transform;
      libraryFocusImage.style.transform = transform;

      const remaining =
        Math.abs(target.x - current.x) +
        Math.abs(target.y - current.y) +
        Math.abs(target.scale - current.scale);
      if (remaining > 0.001) requestLibraryRender();
    };

    const requestLibraryRender = () => {
      if (!animationFrame) animationFrame = requestAnimationFrame(renderLibrary);
    };

    const updatePointerTarget = (event) => {
      const bounds = libraryFrame.getBoundingClientRect();
      const pointerX = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
      const pointerY = clamp((event.clientY - bounds.top) / bounds.height, 0, 1);
      libraryFrame.style.setProperty("--loupe-x", `${(pointerX * 100).toFixed(2)}%`);
      libraryFrame.style.setProperty("--loupe-y", `${(pointerY * 100).toFixed(2)}%`);
      target.x = 0.08 + pointerX * 0.84;
      target.y = 0.1 + pointerY * 0.8;
      requestLibraryRender();
    };

    const updateLibraryMode = () => {
      libraryExplorer.classList.toggle("is-pointer-explorer", libraryMotion.matches);
      if (libraryMotion.matches) {
        requestLibraryRender();
      } else {
        isZoomed = false;
        libraryExplorer.classList.remove("is-zoomed");
        libraryImage.style.removeProperty("transform");
        libraryFocusImage.style.removeProperty("transform");
        libraryFrame.style.removeProperty("--loupe-x");
        libraryFrame.style.removeProperty("--loupe-y");
      }
    };

    libraryFrame.addEventListener("pointerenter", (event) => {
      if (!libraryMotion.matches) return;
      isZoomed = true;
      libraryExplorer.classList.add("is-zoomed");
      updatePointerTarget(event);
    });
    libraryFrame.addEventListener("pointermove", (event) => {
      if (isZoomed) updatePointerTarget(event);
    });
    libraryFrame.addEventListener("pointerleave", () => {
      isZoomed = false;
      libraryExplorer.classList.remove("is-zoomed");
      target.x = overview.x;
      target.y = overview.y;
      libraryFrame.style.setProperty("--loupe-x", "50%");
      libraryFrame.style.setProperty("--loupe-y", "50%");
      requestLibraryRender();
    });
    window.addEventListener("resize", requestLibraryRender, { passive: true });
    libraryMotion.addEventListener("change", updateLibraryMode);
    if (!libraryImage.complete) {
      libraryImage.addEventListener("load", requestLibraryRender, { once: true });
    }
    updateLibraryMode();
  }
})();
